import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import marketplaceAPI from '../services/marketplace.api.js';
import useAuthStore from './authStore';

const useMarketplaceStore = create(
  persist(
    (set, get) => ({
      // State
      userTier: null,
      userStats: null,
      categories: [],
      businesses: [],
      featuredBusinesses: [],
      selectedBusiness: null,
      searchResults: null,

      // Messaging State
      conversations: [],
      selectedConversation: null,
      messages: [],

      // Reviews State
      reviews: [],
      reviewsSummary: null,

      // Favorites State
      favorites: [],

      businessCategories: [],
      productServiceCategories: [],
      subcategories: {},
      allowedProductCategories: [],

      // Orders State
      orders: [],
      recentOrders: [],
      orderCounts: {
        total: 0,
        pending: 0,
        completed: 0,
        cancelled: 0
      },
      selectedOrder: null,
      orderStats: null,

      // Products State
      products: [],

      // Marketplace Messages State
      marketplaceMessages: [],
      marketplaceConversations: [],

      // Inventory State
      inventoryAlerts: [],

      // Business Stats
      businessStats: {},

      // Notifications State
      notifications: {},

      // Business Expiry State
      businessExpiry: null,
      userBusiness: null,

      // Pagination
      pagination: null,

      // UI State
      isLoading: false,
      error: null,

      // =================== BUSINESS EXPIRY ACTIONS ===================

      // Set user business data
      setUserBusiness: (business) => set({ userBusiness: business }),

      // Set business expiry data
      setBusinessExpiry: (expiry) => set({ businessExpiry: expiry }),

      // Update business expiry (for renewal)
      updateBusinessExpiry: (data) => set(state => ({
        userBusiness: state.userBusiness ? {
          ...state.userBusiness,
          expiresAt: data.expiresAt,
          isExpired: false,
          hoursLeft: data.hoursLeft || 24,
          percentageLeft: data.percentageLeft || 100
        } : null,
        businessExpiry: {
          ...state.businessExpiry,
          expiresAt: data.expiresAt,
          isExpired: false,
          hoursLeft: data.hoursLeft || 24,
          percentageLeft: data.percentageLeft || 100
        }
      })),

      // Clear user business data
      clearUserBusiness: () => set({ userBusiness: null, businessExpiry: null }),

      // =================== REAL-TIME UPDATE METHODS ===================

      // Real-time order methods
      addRealTimeOrder: (order) => {
        set(state => ({
          orders: [order, ...(state.orders || [])].slice(0, 50), // Keep last 50 orders
          recentOrders: [order, ...(state.recentOrders || [])].slice(0, 10),
          orderCounts: {
            total: (state.orderCounts?.total || 0) + 1,
            pending: order.status === 'PENDING'
              ? (state.orderCounts?.pending || 0) + 1
              : (state.orderCounts?.pending || 0),
            completed: order.status === 'COMPLETED'
              ? (state.orderCounts?.completed || 0) + 1
              : (state.orderCounts?.completed || 0),
            cancelled: order.status === 'CANCELLED'
              ? (state.orderCounts?.cancelled || 0) + 1
              : (state.orderCounts?.cancelled || 0)
          }
        }));
      },

      updateOrderStatusRealTime: (orderId, status, previousStatus = null) => {
        set(state => ({
          orders: (state.orders || []).map(order =>
            order.id === orderId ? { ...order, status } : order
          ),
          recentOrders: (state.recentOrders || []).map(order =>
            order.id === orderId ? { ...order, status } : order
          ),
          selectedOrder: state.selectedOrder?.id === orderId
            ? { ...state.selectedOrder, status }
            : state.selectedOrder,
          orderCounts: {
            ...state.orderCounts,
            pending: previousStatus === 'PENDING' && status !== 'PENDING'
              ? Math.max(0, (state.orderCounts?.pending || 1) - 1)
              : previousStatus !== 'PENDING' && status === 'PENDING'
                ? (state.orderCounts?.pending || 0) + 1
                : state.orderCounts?.pending || 0,
            completed: previousStatus !== 'COMPLETED' && status === 'COMPLETED'
              ? (state.orderCounts?.completed || 0) + 1
              : previousStatus === 'COMPLETED' && status !== 'COMPLETED'
                ? Math.max(0, (state.orderCounts?.completed || 1) - 1)
                : state.orderCounts?.completed || 0,
            cancelled: previousStatus !== 'CANCELLED' && status === 'CANCELLED'
              ? (state.orderCounts?.cancelled || 0) + 1
              : previousStatus === 'CANCELLED' && status !== 'CANCELLED'
                ? Math.max(0, (state.orderCounts?.cancelled || 1) - 1)
                : state.orderCounts?.cancelled || 0
          }
        }));
      },

      updateOrderCounts: (businessId, data) => {
        set(state => ({
          businessStats: {
            ...state.businessStats,
            [businessId]: {
              ...(state.businessStats?.[businessId] || {}),
              orderCount: (state.businessStats?.[businessId]?.orderCount || 0) + 1,
              lastOrderAt: new Date().toISOString(),
              lastOrderAmount: data.totalAmount
            }
          }
        }));
      },

      // Real-time product methods
      addRealTimeProduct: (product) => {
        set(state => ({
          products: [product, ...(state.products || [])].slice(0, 50),
          businessStats: {
            ...state.businessStats,
            [product.businessId]: {
              ...(state.businessStats?.[product.businessId] || {}),
              productCount: (state.businessStats?.[product.businessId]?.productCount || 0) + 1
            }
          }
        }));

        // Fix: Get current state using get() instead of using 'state' variable
        const currentState = get();
        if (currentState.selectedBusiness?.id === product.businessId) {
          set(state => ({
            selectedBusiness: {
              ...state.selectedBusiness,
              products: [product, ...(state.selectedBusiness.products || [])],
              _count: {
                ...state.selectedBusiness._count,
                products: (state.selectedBusiness._count?.products || 0) + 1
              }
            }
          }));
        }
      },

      updateProductRealTime: (productId, updates) => {
        set(state => {
          // Update in products array
          const updatedProducts = (state.products || []).map(product =>
            product.id === productId ? { ...product, ...updates } : product
          );

          // Update in selected business if applicable
          let updatedSelectedBusiness = state.selectedBusiness;
          if (state.selectedBusiness?.id === updates.businessId) {
            updatedSelectedBusiness = {
              ...state.selectedBusiness,
              products: state.selectedBusiness.products?.map(product =>
                product.id === productId ? { ...product, ...updates } : product
              )
            };
          }

          return {
            products: updatedProducts,
            selectedBusiness: updatedSelectedBusiness
          };
        });
      },

      // Inventory alert methods
      addInventoryAlert: (alert) => {
        set(state => ({
          inventoryAlerts: [alert, ...(state.inventoryAlerts || [])].slice(0, 20)
        }));
      },

      // Marketplace message methods
      addMarketplaceMessage: (message) => {
        set(state => {
          // Add to messages array
          const newMessages = [...(state.marketplaceMessages || []), message];

          // Update conversation unread counts
          const conversations = (state.marketplaceConversations || []).map(conv => {
            if (conv.id === message.conversationId) {
              const currentUser = useAuthStore.getState().user;
              const isFromOther = message.senderId !== currentUser?.id;

              return {
                ...conv,
                unreadCount: isFromOther ? (conv.unreadCount || 0) + 1 : conv.unreadCount,
                lastMessage: message,
                lastMessageAt: message.createdAt || new Date().toISOString()
              };
            }
            return conv;
          });

          // Sort conversations by last message time
          const sortedConversations = [...conversations].sort((a, b) =>
            new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0)
          );

          return {
            marketplaceMessages: newMessages.slice(-100), // Keep last 100 messages
            marketplaceConversations: sortedConversations
          };
        });
      },

      // Business stats updates
      updateBusinessStats: (businessId, stats) => {
        set(state => ({
          businessStats: {
            ...state.businessStats,
            [businessId]: {
              ...(state.businessStats?.[businessId] || {}),
              ...stats,
              updatedAt: new Date().toISOString()
            }
          }
        }));
      },

      // Business expiry real-time update
      updateBusinessExpiryRealTime: (data) => {
        set(state => ({
          userBusiness: state.userBusiness ? {
            ...state.userBusiness,
            expiresAt: data.expiresAt,
            isExpired: false,
            hoursLeft: 24
          } : null,
          businessExpiry: {
            ...state.businessExpiry,
            expiresAt: data.expiresAt,
            isExpired: false,
            hoursLeft: 24,
            percentageLeft: 100
          }
        }));
      },

      // Clear all real-time data (useful for logout)
      clearRealTimeData: () => {
        set({
          orders: [],
          recentOrders: [],
          products: [],
          marketplaceMessages: [],
          marketplaceConversations: [],
          inventoryAlerts: [],
          businessStats: {},
          orderCounts: { total: 0, pending: 0, completed: 0, cancelled: 0 }
        });
      },

      // =================== ORIGINAL ACTIONS ===================

      // Actions
      getUserTier: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getUserTier();
          set({ userTier: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get user tier error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getUserStats: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getUserStats();
          set({ userStats: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get user stats error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getCategories: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getCategories();
          set({ categories: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get categories error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      createBusiness: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.createBusiness(data);

          // Add to businesses list
          set(state => ({
            businesses: [response.data, ...state.businesses],
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      searchBusinesses: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.searchBusinesses(params);

          set({
            businesses: response.data.businesses,
            pagination: response.data.pagination,
            searchResults: response.data.businesses.length > 0 ? 'found' : 'not_found'
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getFeaturedBusinesses: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getFeaturedBusinesses();
          set({ featuredBusinesses: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get featured businesses error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getBusinessDetails: async (businessId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getBusinessDetails(businessId);
          set({ selectedBusiness: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Messaging Actions
      sendMessage: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.sendMessage(data);

          // Add to messages if in same conversation
          const state = get();
          if (state.selectedConversation?.id === data.conversationId) {
            set(state => ({
              messages: [...state.messages, response.data]
            }));
          }

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getConversations: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getConversations(params);

          set({
            conversations: response.data.conversations,
            pagination: response.data.pagination
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get conversations error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getMessages: async (conversationId, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getMessages(conversationId, params);

          set({
            messages: response.data.messages,
            selectedConversation: response.data.conversation,
            pagination: response.data.pagination
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Reviews Actions
      createReview: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.createReview(data);

          // If we're on a business profile page, refresh the business details
          const state = get();
          if (state.selectedBusiness?.id === data.businessId) {
            // Refresh business details to get updated rating
            await state.getBusinessDetails(data.businessId);
            // Refresh reviews
            await state.getReviews({ businessId: data.businessId });
          }

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getReviews: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getReviews(params);

          set({
            reviews: response.data.reviews,
            reviewsSummary: response.data.summary,
            pagination: response.data.pagination
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get reviews error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      addSellerResponse: async (reviewId, response) => {
        set({ isLoading: true, error: null });
        try {
          const result = await marketplaceAPI.addSellerResponse(reviewId, response);

          // Update review in state
          set(state => ({
            reviews: state.reviews.map(review =>
              review.id === reviewId ? result.data : review
            )
          }));

          return result.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== ORDER ACTIONS ===================
      createOrder: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.createOrder(data);
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getUserOrders: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getUserOrders(params);
          set({ orders: response.data.orders });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get orders error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getOrderDetails: async (orderId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getOrderDetails(orderId);
          set({ selectedOrder: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateOrderStatus: async (orderId, status) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.updateOrderStatus(orderId, status);

          // Update order in state
          set(state => ({
            orders: state.orders.map(order =>
              order.id === orderId ? { ...order, status } : order
            ),
            selectedOrder: state.selectedOrder?.id === orderId
              ? { ...state.selectedOrder, status }
              : state.selectedOrder
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      canReviewOrder: async (orderId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.canReviewOrder(orderId);
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Can review order error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== PRODUCT ACTIONS ===================
      createProduct: async (businessId, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.createProduct(businessId, data);

          // Update selected business products if viewing it
          set(state => ({
            selectedBusiness: state.selectedBusiness ? {
              ...state.selectedBusiness,
              products: [response.data, ...(state.selectedBusiness.products || [])],
              _count: {
                ...state.selectedBusiness._count,
                products: (state.selectedBusiness._count?.products || 0) + 1
              }
            } : null,
            businesses: state.businesses.map(business =>
              business.id === businessId
                ? { ...business, _count: { ...business._count, products: (business._count?.products || 0) + 1 } }
                : business
            )
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getProducts: async (businessId, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getProducts(businessId, params);
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get products error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateProduct: async (productId, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.updateProduct(productId, data);

          // Update product in state
          set(state => ({
            selectedBusiness: state.selectedBusiness ? {
              ...state.selectedBusiness,
              products: state.selectedBusiness.products?.map(product =>
                product.id === productId ? { ...product, ...response.data } : product
              )
            } : null
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      deleteProduct: async (productId) => {
        set({ isLoading: true, error: null });
        try {
          await marketplaceAPI.deleteProduct(productId);

          // Remove from state
          set(state => ({
            selectedBusiness: state.selectedBusiness ? {
              ...state.selectedBusiness,
              products: state.selectedBusiness.products?.filter(p => p.id !== productId),
              _count: {
                ...state.selectedBusiness._count,
                products: Math.max(0, (state.selectedBusiness._count?.products || 0) - 1)
              }
            } : null
          }));

          return { success: true };
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== SERVICE ACTIONS ===================
      createService: async (businessId, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.createService(businessId, data);

          // Update selected business services if viewing it
          set(state => ({
            selectedBusiness: state.selectedBusiness ? {
              ...state.selectedBusiness,
              services: [response.data, ...(state.selectedBusiness.services || [])],
              _count: {
                ...state.selectedBusiness._count,
                services: (state.selectedBusiness._count?.services || 0) + 1
              }
            } : null,
            businesses: state.businesses.map(business =>
              business.id === businessId
                ? { ...business, _count: { ...business._count, services: (business._count?.services || 0) + 1 } }
                : business
            )
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getServices: async (businessId, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getServices(businessId, params);
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get services error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateService: async (serviceId, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.updateService(serviceId, data);

          // Update service in state
          set(state => ({
            selectedBusiness: state.selectedBusiness ? {
              ...state.selectedBusiness,
              services: state.selectedBusiness.services?.map(service =>
                service.id === serviceId ? { ...service, ...response.data } : service
              )
            } : null
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      deleteService: async (serviceId) => {
        set({ isLoading: true, error: null });
        try {
          await marketplaceAPI.deleteService(serviceId);

          // Remove from state
          set(state => ({
            selectedBusiness: state.selectedBusiness ? {
              ...state.selectedBusiness,
              services: state.selectedBusiness.services?.filter(s => s.id !== serviceId),
              _count: {
                ...state.selectedBusiness._count,
                services: Math.max(0, (state.selectedBusiness._count?.services || 0) - 1)
              }
            } : null
          }));

          return { success: true };
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Category actions
      getBusinessCategories: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getBusinessCategories();
          set({ businessCategories: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getProductServiceCategories: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getProductServiceCategories();
          set({ productServiceCategories: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getSubcategories: async (parentCategory) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getSubcategories(parentCategory);
          set(state => ({
            subcategories: {
              ...state.subcategories,
              [parentCategory]: response.data
            }
          }));
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getAllowedProductCategories: async (businessId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getAllowedProductCategories(businessId);
          set({ allowedProductCategories: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== BUSINESS VERIFICATION ===================
      updateBusinessStatus: async (businessId, status) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.updateBusinessStatus(businessId, status);

          // Update in user stats and businesses list
          set(state => ({
            userStats: state.userStats ? {
              ...state.userStats,
              businesses: {
                ...state.userStats.businesses,
                list: state.userStats.businesses.list.map(business =>
                  business.id === businessId ? { ...business, status } : business
                ),
                active: status === 'ACTIVE'
                  ? (state.userStats.businesses.active || 0) + 1
                  : state.userStats.businesses.active
              }
            } : null,
            businesses: state.businesses.map(business =>
              business.id === businessId ? { ...business, status } : business
            )
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateBusiness: async (businessId, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.updateBusiness(businessId, data);

          // Update in all relevant state locations
          set(state => ({
            selectedBusiness: state.selectedBusiness?.id === businessId
              ? { ...state.selectedBusiness, ...response.data }
              : state.selectedBusiness,
            userStats: state.userStats ? {
              ...state.userStats,
              businesses: {
                ...state.userStats.businesses,
                list: state.userStats.businesses.list.map(business =>
                  business.id === businessId ? { ...business, ...response.data } : business
                )
              }
            } : null,
            businesses: state.businesses.map(business =>
              business.id === businessId ? { ...business, ...response.data } : business
            )
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      deleteBusiness: async (businessId) => {
        set({ isLoading: true, error: null });
        try {
          await marketplaceAPI.deleteBusiness(businessId);

          // Remove from all state locations
          set(state => ({
            userStats: state.userStats ? {
              ...state.userStats,
              businesses: {
                ...state.userStats.businesses,
                count: Math.max(0, (state.userStats.businesses.count || 0) - 1),
                active: state.userStats.businesses.list.find(b => b.id === businessId)?.status === 'ACTIVE'
                  ? Math.max(0, (state.userStats.businesses.active || 0) - 1)
                  : state.userStats.businesses.active,
                list: state.userStats.businesses.list.filter(b => b.id !== businessId)
              }
            } : null,
            businesses: state.businesses.filter(b => b.id !== businessId),
            selectedBusiness: state.selectedBusiness?.id === businessId ? null : state.selectedBusiness
          }));

          return { success: true };
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== FAVORITES ===================
      toggleFavorite: async (businessId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.toggleFavorite(businessId);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getFavorites: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getFavorites(params);
          set({ favorites: response.data.favorites });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get favorites error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      checkFavorite: async (businessId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.checkFavorite(businessId);
          return response.isFavorited;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Check favorite error:', message);
          set({ error: message });
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== INVENTORY ACTIONS ===================
      updateInventory: async (productId, quantity, operation = 'set') => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.updateInventory(productId, quantity, operation);

          // Update product in state
          set(state => ({
            selectedBusiness: state.selectedBusiness ? {
              ...state.selectedBusiness,
              products: state.selectedBusiness.products?.map(product =>
                product.id === productId ? { ...product, quantity: response.data.quantity } : product
              )
            } : null
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getInventoryAlerts: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getInventoryAlerts(params);
          set({ inventoryAlerts: response.data.alerts });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get inventory alerts error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== BUSINESS ORDERS ===================
      getBusinessOrders: async (businessId, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getBusinessOrders(businessId, params);
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get business orders error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getOrderStats: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getOrderStats(params);
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get order stats error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      checkCanCreateBusiness: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.checkCanCreateBusiness();
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== BUSINESS EXPIRY & LIMITS ACTIONS ===================
      getBusinessExpiry: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getBusinessExpiry();
          set({ businessExpiry: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getUserBusiness: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getUserBusiness();
          set({ userBusiness: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      checkProductLimit: async (businessId) => {
        try {
          const response = await marketplaceAPI.checkProductLimit(businessId);
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Check product limit error:', message);
          return { canAdd: false, error: message };
        }
      },

      checkServiceLimit: async (businessId) => {
        try {
          const response = await marketplaceAPI.checkServiceLimit(businessId);
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Check service limit error:', message);
          return { canAdd: false, error: message };
        }
      },

      // Notifications Actions
      getNotifications: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.getMarketplaceNotifications(params);

          set({ notifications: response.data.notifications });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get notifications error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      markNotificationsAsRead: async (notificationIds = []) => {
        set({ isLoading: true, error: null });
        try {
          const response = await marketplaceAPI.markNotificationsAsRead(notificationIds);

          // Update notifications in state
          set(state => {
            const newNotifications = { ...state.notifications };
            Object.keys(newNotifications).forEach(date => {
              newNotifications[date] = newNotifications[date].map(notification => ({
                ...notification,
                read: notificationIds.length === 0 || notificationIds.includes(notification.id) ? true : notification.read
              }));
            });

            return { notifications: newNotifications };
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Utility Actions
      clearSearchResults: () => {
        set({
          searchResults: null,
          pagination: null
        });
      },

      clearError: () => set({ error: null })
    }),
    {
      name: 'marketplace-storage',
      partialize: (state) => ({
        userTier: state.userTier,
        userStats: state.userStats,
        categories: state.categories,
        favorites: state.favorites,
        orderCounts: state.orderCounts,
        businessStats: state.businessStats,
        businessExpiry: state.businessExpiry,
        userBusiness: state.userBusiness,
        businessCategories: state.businessCategories,
        productServiceCategories: state.productServiceCategories
      })
    }
  )
);

export default useMarketplaceStore;